import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  NgZone,
  output,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { AIStatus, AIThoughtInterface } from '@vendasta/conversation';
import { Citation, CitationType, METADATA_KEY_CITATIONS } from '../citations/citations';
import { AiKnowledgeModule, BUSINESS_PROFILE_URL_TOKEN, MANAGE_KNOWLEDGE_URL_TOKEN } from '@galaxy/ai-knowledge';
import { AiProposedActionComponent, ProposedActionApproval, ProposedActionRejection } from '@galaxy/ai-assistant';
import { toSignal } from '@angular/core/rxjs-interop';
import {
  ACCOUNT_GROUP_ID_TOKEN,
  CONVERSATION_HOST_APP_INTERFACE_TOKEN,
  ConversationMessage,
  ConversationService,
  getAccountGroupIDFromConversation,
  getConversationIdFromDetail,
  KeyValuePair,
} from '../../../../../../../core/src';
import { MatIconModule } from '@angular/material/icon';

import { TranslateModule } from '@ngx-translate/core';
import {
  WebsitePreviewModalComponent,
  WebsitePreviewModalData,
} from '../citations/website-preview-modal/website-preview-modal.component';
import {
  FunctionCallModalComponent,
  FunctionCallModalData,
} from '../citations/function-call/function-call-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import {
  CitationAdapter,
  FunctionComplete,
  FunctionCompleteCitation,
  FunctionStart,
  FunctionStartCitation,
} from './ai-thinking-chunk-types';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { BadgeComponent } from '@vendasta/galaxy/badge';
import { ConversationPipesModule } from '../../../../../../../pipes';
import DOMPurify from 'dompurify';
import { FormatMessagePipe } from '../../../../../../../pipes/src/format-message-pipe';
import { approveProposedAction$, rejectProposedAction$ } from '@vendasta/protos/conversation/v1/conversation-aiservice';
import { AIMode } from '@vendasta/protos/conversation/v1/enums';
import { catchError, EMPTY, take } from 'rxjs';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';

enum ThoughtType {
  REASONING = 'reasoning',
  FUNCTION_START = 'function_start',
  FUNCTION_COMPLETE = 'function_complete',
  PROPOSED_ACTION = 'proposed_action',
}

enum ThoughtState {
  RUNNING = 'running',
  FINISHED = 'finished',
}

interface ProposedActionContent {
  session_event_id: string;
  namespace_id: string;
  namespace_type: string;
}

interface ThoughtUIModel {
  type: ThoughtType;
  sequenceNumber?: number;
  content: AIThoughtInterface;
  state?: ThoughtState;
  functionName?: string;
  formattedContent?: string;
  proposedActionEventId?: string;
  proposedActionNamespaceId?: string;
  proposedActionNamespaceType?: string;
}

@Component({
  selector: 'inbox-ai-thinking-window',
  templateUrl: './inbox-ai-thinking-window.component.html',
  styleUrls: ['./inbox-ai-thinking-window.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    MatIconModule,
    MatDividerModule,
    TranslateModule,
    AiKnowledgeModule,
    MatButtonModule,
    MatTooltipModule,
    BadgeComponent,
    ConversationPipesModule,
    AiProposedActionComponent,
  ],
})
export class InboxAiThinkingWindowComponent {
  readonly messageAiThoughts = input<AIThoughtInterface[]>([]);
  readonly messageAiStatus = input<AIStatus>(AIStatus.AI_INVALID);
  readonly messageMetadata = input<KeyValuePair[]>([]);
  readonly message = input<ConversationMessage>();
  readonly hideCitations = input<boolean>(false);
  readonly latestUserMessageTime = input<Date | undefined>(undefined);

  readonly proposedActionResolved = output<void>();

  CitationType = CitationType;
  ThoughtType = ThoughtType;
  ThoughtState = ThoughtState;

  private static readonly CHARS_PER_TICK = 12;
  private static readonly SCROLL_THROTTLE_MS = 200;

  private readonly elementRef = inject(ElementRef);
  private readonly streamingBody = viewChild<ElementRef>('streamingBody');
  private readonly userToggledOpen = signal(false);
  private readonly revealedLengths = signal<Map<number, number>>(new Map());
  private readonly ngZone = inject(NgZone);
  private readonly destroyRef = inject(DestroyRef);
  private revealTimer: ReturnType<typeof setInterval> | null = null;
  private lastScrollTime = 0;

  readonly isThinkingState = computed(() => {
    const status = this.messageAiStatus();
    return status === AIStatus.AI_RECEIVED || status === AIStatus.AI_THINKING;
  });

  readonly isCollapsed = computed(() => {
    if (this.isThinkingState()) return false;
    return !this.userToggledOpen();
  });

  private readonly conversationService = inject(ConversationService);
  private readonly hostAppInterface = inject(CONVERSATION_HOST_APP_INTERFACE_TOKEN);
  private readonly dialog = inject(MatDialog);
  private readonly snackbarService = inject(SnackbarService);
  private readonly isMultiLocation = this.hostAppInterface.getAppOptions().is_multilocation;
  private conversationDetails = toSignal(this.conversationService.currentConversationDetail$);
  private accountGroupIdToken = toSignal(inject(ACCOUNT_GROUP_ID_TOKEN));
  private readonly formatPipe = new FormatMessagePipe();
  private readonly formattedCache = new Map<number, { length: number; html: string }>();
  private previousThinkingState = false;

  private accountGroupId = computed(() => {
    if (this.isMultiLocation) {
      return getAccountGroupIDFromConversation(this.conversationDetails()?.conversation);
    }
    return this.accountGroupIdToken() || '';
  });

  private knowledgeURLToken = toSignal(inject(MANAGE_KNOWLEDGE_URL_TOKEN));
  private knowledgeURL = computed(() => {
    if (this.isMultiLocation) {
      return `/account/location/${this.accountGroupId()}/settings/ai-knowledge`;
    }
    return this.knowledgeURLToken() || '';
  });

  private businessProfileURLToken = toSignal(inject(BUSINESS_PROFILE_URL_TOKEN));
  private businessProfileURL = computed(() => {
    if (this.isMultiLocation) {
      return `/account/location/${this.accountGroupId()}/settings/profile`;
    }
    return this.businessProfileURLToken() || '';
  });

  protected conversationId = computed(() => getConversationIdFromDetail(this.conversationDetails()));

  protected readonly proposedActions = computed(() => {
    return this.thoughtsUIModel().filter((t) => t.type === ThoughtType.PROPOSED_ACTION);
  });

  protected readonly proposedActionsSuperseded = computed(() => {
    const userMessageTime = this.latestUserMessageTime();
    const messageTime = this.message()?.created;
    if (!userMessageTime || !messageTime) {
      return false;
    }
    return userMessageTime > messageTime;
  });

  protected readonly regularThoughts = computed(() => {
    return this.thoughtsUIModel().filter((t) => t.type !== ThoughtType.PROPOSED_ACTION);
  });

  protected readonly hasRegularThoughtOrCitation = computed(() => {
    return this.regularThoughts().length > 0 || (!this.hideCitations() && (this.citationsUIModel()?.length ?? 0) > 0);
  });

  constructor() {
    effect(() => {
      const thinking = this.isThinkingState();
      if (this.previousThinkingState && !thinking) {
        untracked(() => {
          this.stopRevealTimer();
          this.formattedCache.clear();
          this.userToggledOpen.set(false);
          this.scrollToResponse();
        });
      }
      if (thinking && !this.previousThinkingState) {
        untracked(() => {
          this.revealedLengths.set(new Map());
          this.startRevealTimer();
        });
      }
      this.previousThinkingState = thinking;
    });

    effect(() => {
      this.thoughtsUIModel();
      const body = this.streamingBody()?.nativeElement;
      if (body && this.isThinkingState()) {
        const now = Date.now();
        if (now - this.lastScrollTime >= InboxAiThinkingWindowComponent.SCROLL_THROTTLE_MS) {
          this.lastScrollTime = now;
          this.ngZone.runOutsideAngular(() => requestAnimationFrame(() => (body.scrollTop = body.scrollHeight)));
        }
      }
    });

    this.destroyRef.onDestroy(() => this.stopRevealTimer());
  }

  private startRevealTimer(): void {
    if (this.revealTimer) return;
    this.ngZone.runOutsideAngular(() => {
      this.revealTimer = setInterval(() => this.revealTick(), 32);
    });
  }

  private stopRevealTimer(): void {
    if (this.revealTimer) {
      clearInterval(this.revealTimer);
      this.revealTimer = null;
    }
  }

  private revealTick(): void {
    const thoughts = this.messageAiThoughts() ?? [];
    const current = this.revealedLengths();
    const updated = new Map(current);
    let changed = false;

    for (const thought of thoughts) {
      if (thought.thoughtType !== ThoughtType.REASONING) continue;
      const seq = thought.sequenceNumber ?? 0;
      const totalLen = thought.content?.length ?? 0;
      const revealed = updated.get(seq) ?? 0;
      if (revealed < totalLen) {
        updated.set(seq, Math.min(revealed + InboxAiThinkingWindowComponent.CHARS_PER_TICK, totalLen));
        changed = true;
        break;
      }
    }

    if (changed) {
      this.ngZone.run(() => this.revealedLengths.set(updated));
    }
  }

  toggleCollapse(): void {
    this.userToggledOpen.update((prev) => !prev);
  }

  private scrollToResponse(): void {
    const thinkingEl = this.elementRef.nativeElement;
    const responseEl = thinkingEl.nextElementSibling as HTMLElement;
    if (!responseEl) return;

    setTimeout(() => {
      const chatContainer = thinkingEl.closest('glxy-chat-container') as HTMLElement;
      if (!chatContainer) {
        responseEl.scrollIntoView({ block: 'start', behavior: 'instant' });
        return;
      }

      const containerRect = chatContainer.getBoundingClientRect();
      const responseRect = responseEl.getBoundingClientRect();
      const scrollOffset = responseRect.top - containerRect.top + chatContainer.scrollTop;

      const isIOS =
        /iPad|iPhone|iPod/.test(navigator.userAgent) ||
        (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
      chatContainer.scrollTo({
        top: scrollOffset,
        behavior: isIOS ? 'instant' : 'smooth',
      });
    }, 220);
  }

  readonly thoughtsUIModel = computed<ThoughtUIModel[]>(() => {
    const thoughts = this.messageAiThoughts() ?? [];
    const thoughtsUIModel: ThoughtUIModel[] = [];
    const isStreaming = this.isThinkingState();
    const revealed = this.revealedLengths();
    let pendingReveal = false;

    for (const thought of thoughts) {
      const type = thought.thoughtType;

      if (isStreaming && pendingReveal) break;

      switch (type) {
        case ThoughtType.FUNCTION_START: {
          thoughtsUIModel.push({
            type: ThoughtType.FUNCTION_START,
            sequenceNumber: thought.sequenceNumber,
            content: thought,
            state: ThoughtState.RUNNING,
            functionName: this.parseFunctionName(thought.content, 'FunctionStart'),
          });
          break;
        }
        case ThoughtType.FUNCTION_COMPLETE: {
          const functionName = this.parseFunctionName(thought.content, 'FunctionComplete');
          const previousStartedFunction = thoughtsUIModel.find(
            (item) =>
              item.type === ThoughtType.FUNCTION_START &&
              item.functionName === functionName &&
              item.state === ThoughtState.RUNNING,
          );
          if (previousStartedFunction) {
            previousStartedFunction.state = ThoughtState.FINISHED;
            previousStartedFunction.content = thought;
          }
          break;
        }
        case ThoughtType.REASONING: {
          const seq = thought.sequenceNumber ?? 0;
          const rawContent = thought.content ?? '';
          const visibleLength = isStreaming ? (revealed.get(seq) ?? 0) : rawContent.length;

          if (visibleLength === 0) break;

          if (isStreaming && visibleLength < rawContent.length) {
            pendingReveal = true;
          }

          const visibleContent = rawContent.substring(0, visibleLength);
          const cached = this.formattedCache.get(seq);
          let formatted: string;
          if (cached && cached.length === visibleLength) {
            formatted = cached.html;
          } else {
            const cleanedContent = this.escapeHtmlTags(visibleContent);
            const rendered = this.formatPipe.transform(cleanedContent, this.message(), []);
            formatted = this.sanitizeContent(rendered);
            this.formattedCache.set(seq, { length: visibleLength, html: formatted });
          }
          thoughtsUIModel.push({
            type: ThoughtType.REASONING,
            sequenceNumber: thought.sequenceNumber,
            content: thought,
            formattedContent: formatted,
          });
          break;
        }
        case ThoughtType.PROPOSED_ACTION: {
          const parsed = this.parseProposedActionContent(thought.content);
          if (parsed) {
            thoughtsUIModel.push({
              type: ThoughtType.PROPOSED_ACTION,
              sequenceNumber: thought.sequenceNumber,
              content: thought,
              proposedActionEventId: parsed.session_event_id,
              proposedActionNamespaceId: parsed.namespace_id,
              proposedActionNamespaceType: parsed.namespace_type,
            });
          }
          break;
        }
        default:
          console.error(`Unsupported thought type: ${type}`);
      }
    }
    return thoughtsUIModel;
  });

  readonly citationsUIModel = computed(() => {
    const citations = this.citations() ?? [];
    // Function calls are filtered out because they are displayed in the thoughts
    return citations.filter((citation) => citation.type !== CitationType.TYPE_FUNCTION_CALL);
  });

  citations = computed(() => {
    if (!this.messageMetadata()) return [];
    for (const m of this.messageMetadata()) {
      if (m.key !== METADATA_KEY_CITATIONS) continue;

      if (m.value && m.value.length > 0) {
        const rawCitations = JSON.parse(m.value) as Citation[];
        // Citations for the same source should be deduped because the specific content cited is not a part of the UI citation
        // However, we should include separate citations for each function call since they show the full arguments and response of each call
        const dedupedCitations: Citation[] = [];
        for (const citation of rawCitations) {
          if (citation.type === CitationType.TYPE_FUNCTION_CALL) {
            dedupedCitations.push(citation);
            continue;
          }

          const isDuplicate = dedupedCitations.find((c) => {
            return c.type === citation.type && c.title === citation.title;
          });

          if (!isDuplicate) {
            citation.link = this.buildCitationLink(citation.type ?? 0, citation.link);
            dedupedCitations.push(citation);
          }
        }
        return dedupedCitations;
      }

      break;
    }

    return [];
  });

  private buildCitationLink(type: CitationType, id: string): string {
    switch (type) {
      case CitationType.TYPE_INVALID:
        return id;
      case CitationType.TYPE_WEBSITE:
      case CitationType.TYPE_WEBSITE_V2:
        return id;
      case CitationType.TYPE_BUSINESS_PROFILE:
      case CitationType.TYPE_BUSINESS_PROFILE_V2: {
        return this.businessProfileURL();
      }
      case CitationType.TYPE_USER_INPUT:
      case CitationType.TYPE_USER_INPUT_V2: {
        return `${this.knowledgeURL()}?editSource=${id}`;
      }
      case CitationType.TYPE_FILE: {
        return `${this.knowledgeURL()}?editSource=${id}`;
      }
      default:
        return id;
    }
  }

  supportsFilePreview(citation: Citation): boolean {
    return citation.type === CitationType.TYPE_WEBSITE || citation.type === CitationType.TYPE_WEBSITE_V2;
  }

  openWebsitePreview(citation: Citation): void {
    if (!citation.fileUrl || !this.supportsFilePreview(citation)) return;

    this.dialog.open(WebsitePreviewModalComponent, {
      data: {
        fileUrl: citation.fileUrl,
        fileName: citation.title,
        liveUrl: citation.link,
        citationType: citation.type,
      } as WebsitePreviewModalData,
      panelClass: 'mat-dialog-no-padding',
      autoFocus: false,
    });
  }

  openFunctionCallDetails(thought: AIThoughtInterface): void {
    const modalData = this.thoughtToModalData(thought);
    this.dialog.open(FunctionCallModalComponent, {
      width: '600px',
      maxWidth: '100vw',
      maxHeight: '100vh',
      data: modalData,
      autoFocus: false,
    });
  }

  private thoughtToModalData(thought: AIThoughtInterface): FunctionCallModalData {
    const content = JSON.parse(thought.content ?? '{}');
    const adapter = this.createCitationAdapter(thought.thoughtType ?? '', content);

    return {
      sessionEventId: adapter.sessionEventId() ?? '',
      conversationId: this.conversationId(),
      namespaceType: adapter.namespaceType() ?? '',
      namespaceID: adapter.namespaceID() ?? '',
      title: adapter.title() ?? '',
      description: adapter.description() ?? '',
    };
  }

  private createCitationAdapter(thoughtType: string, content: any): CitationAdapter {
    if (thoughtType === ThoughtType.FUNCTION_START) {
      return new FunctionStartCitation(content.FunctionStart as FunctionStart);
    } else if (thoughtType === ThoughtType.FUNCTION_COMPLETE) {
      return new FunctionCompleteCitation(content.FunctionComplete as FunctionComplete);
    }

    throw new Error(`Unsupported thought type: ${thoughtType}`);
  }

  private parseFunctionName(rawContent: string | undefined, key: 'FunctionStart' | 'FunctionComplete'): string {
    try {
      const content = JSON.parse(rawContent ?? '{}');
      return content[key]?.function_name ?? 'Unknown';
    } catch {
      return 'Unknown';
    }
  }

  private parseProposedActionContent(rawContent: string | undefined): ProposedActionContent | null {
    try {
      const eventContent: { ProposedAction: ProposedActionContent } = JSON.parse(rawContent ?? '{}');
      return eventContent?.ProposedAction?.session_event_id ? eventContent?.ProposedAction : null;
    } catch {
      return null;
    }
  }

  protected onProposedActionApproved(event: ProposedActionApproval, actionComponent: AiProposedActionComponent): void {
    approveProposedAction$({
      conversationId: this.conversationId(),
      proposedActionEventId: event.eventId,
      aiMode: AIMode.AI_MODE_THINKING,
      aiParticipantId: this.message()?.sender?.participantId,
    })
      .pipe(
        take(1),
        catchError(() => {
          actionComponent.resetActionInProgress();
          this.snackbarService.openErrorSnack('INBOX.SOMETHING_WENT_WRONG');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        actionComponent.reload();
        this.proposedActionResolved.emit();
      });
  }

  protected onProposedActionRejected(event: ProposedActionRejection, actionComponent: AiProposedActionComponent): void {
    rejectProposedAction$({
      conversationId: this.conversationId(),
      proposedActionEventId: event.eventId,
      feedback: event.feedback,
      aiMode: AIMode.AI_MODE_THINKING,
      aiParticipantId: this.message()?.sender?.participantId,
    })
      .pipe(
        take(1),
        catchError(() => {
          actionComponent.resetActionInProgress();
          this.snackbarService.openErrorSnack('INBOX.SOMETHING_WENT_WRONG');
          return EMPTY;
        }),
      )
      .subscribe(() => {
        actionComponent.reload();
        this.proposedActionResolved.emit();
      });
  }

  private escapeHtmlTags(content: string | undefined): string {
    if (!content) return '';
    return content.replace(/<(\/?[a-zA-Z][a-zA-Z0-9_-]*)[^>]*>/g, '&lt;$1&gt;');
  }

  protected sanitizeContent(messageText: string): string {
    let allowedAttr: string[] = [];
    const isSafeTarget = messageText.includes('target="_blank"') && messageText.includes('rel="noopener noreferrer"');
    if (isSafeTarget) {
      allowedAttr = ['target', 'rel'];
    }
    return DOMPurify.sanitize(messageText, { ADD_ATTR: allowedAttr });
  }
}
